'use client';

/**
 * ExerciseProgressCard — per-exercise status on the Training sub-tab.
 *
 * Surfaces three signals that were previously buried four taps deep in
 * Intel synthesis:
 *
 *   1. Progressing lifts (intelligence.progressions.weights.ready) —
 *      exercises where the engine suggests moving up.
 *   2. Stalled lifts (intelligence.predictions.plateauWarnings) —
 *      exercises that haven't progressed in N weeks.
 *   3. Decreasing lifts (intelligence.progressions.weights.decreasing) —
 *      exercises trending down despite RPE creep (overreach signal).
 *
 * Each row gets a direction arrow, current weight, plain-English reason,
 * and a suggested next weight if applicable.
 *
 * Reentry-aware: during a returning state, this card collapses to a
 * single line acknowledging that per-exercise signals don't apply — you
 * can't plateau if you haven't trained.
 */

import { TrendingUp, TrendingDown, Minus, ArrowRight } from 'lucide-react';
import type { IntelligenceReport } from '@/lib/unifiedIntelligence';
import type { ReentryState } from '@/lib/reentryState';
import type { AcceptedReentryPlan } from '@/lib/acceptedReentryPlan';
import OpsPanel from './OpsPanel';

interface Props {
  intelligence: IntelligenceReport;
  reentry?: ReentryState | null;
  acceptedPlan?: AcceptedReentryPlan | null;
  /** Maximum rows to show. Default 5. Remaining stay in Intel sub-tab. */
  limit?: number;
}

type Status = 'progressing' | 'stalled' | 'decreasing';

interface Row {
  exercise: string;
  status: Status;
  currentWeight: number | null;
  suggestedWeight: number | null;
  reason: string;
  weeksSince?: number;
}

const STATUS_TOKENS: Record<Status, { tw: string; bg: string; Icon: typeof TrendingUp; label: string }> = {
  progressing: { tw: 'text-emerald-400', bg: 'bg-emerald-500/[0.06]', Icon: TrendingUp, label: 'UP' },
  stalled: { tw: 'text-amber-400', bg: 'bg-amber-500/[0.06]', Icon: Minus, label: 'STALL' },
  decreasing: { tw: 'text-red-400', bg: 'bg-red-500/[0.06]', Icon: TrendingDown, label: 'DOWN' },
};

export default function ExerciseProgressCard({ intelligence, reentry, acceptedPlan, limit = 5 }: Props) {
  const isReentering = reentry?.status === 'returning' || !!acceptedPlan;
  if (isReentering) {
    return (
      <OpsPanel badge="Exercise Progress" code="SYS·EXP·06">
        <p className="font-mono text-[11px] text-slate-500 leading-relaxed">
          Per-exercise progress signals (progression / plateau / regression) need recent sessions to read.
          Resume training and these will populate after a couple of workouts.
        </p>
      </OpsPanel>
    );
  }

  // Combine the three signals into a unified ordered list. Order:
  //   stalled (action needed) → decreasing (overreach signal) → progressing (wins).
  // Within each bucket, sort by impact: stalled by weeksSince DESC, others by name.
  const rows: Row[] = [];

  const seen = new Set<string>();
  const norm = (n: string) => n.trim().toLowerCase();

  for (const p of intelligence.predictions.plateauWarnings ?? []) {
    if (seen.has(norm(p.exercise))) continue;
    seen.add(norm(p.exercise));
    rows.push({
      exercise: p.exercise,
      status: 'stalled',
      currentWeight: null,
      suggestedWeight: null,
      reason: p.suggestedIntervention,
      weeksSince: p.weeksSinceProgress,
    });
  }

  for (const d of intelligence.progressions.weights?.decreasing ?? []) {
    if (seen.has(norm(d.exerciseName))) continue;
    seen.add(norm(d.exerciseName));
    rows.push({
      exercise: d.exerciseName,
      status: 'decreasing',
      currentWeight: d.currentWeight,
      suggestedWeight: d.suggestedWeight,
      reason: d.reason,
    });
  }

  for (const r of intelligence.progressions.weights?.ready ?? []) {
    if (seen.has(norm(r.exerciseName))) continue;
    seen.add(norm(r.exerciseName));
    rows.push({
      exercise: r.exerciseName,
      status: 'progressing',
      currentWeight: r.currentWeight,
      suggestedWeight: r.suggestedWeight,
      reason: r.reason,
    });
  }

  if (rows.length === 0) {
    return null;
  }

  const totalTracked = intelligence.progressions.weights?.totalTracked ?? 0;
  const visible = rows.slice(0, limit);
  const overflow = rows.length - visible.length;

  return (
    <OpsPanel
      badge="Exercise Progress"
      code="SYS·EXP·06"
      callout={<span className="text-slate-400">{rows.length}<span className="text-slate-700">/</span>{totalTracked || rows.length}</span>}
    >
      <ul className="space-y-1.5">
        {visible.map((row, i) => {
          const t = STATUS_TOKENS[row.status];
          return (
            <li key={i} className={`relative rounded border border-slate-800 ${t.bg} px-3 py-2`}>
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <div className="flex items-baseline gap-2 min-w-0">
                  <t.Icon size={14} className={`${t.tw} flex-shrink-0 translate-y-[1px]`} aria-hidden />
                  <span className="font-mono text-[12px] font-bold text-white truncate">{row.exercise}</span>
                  <span className={`font-mono text-[9px] uppercase tracking-[0.22em] font-bold ${t.tw}`}>
                    {t.label}
                    {row.status === 'stalled' && row.weeksSince ? ` · ${row.weeksSince}w` : ''}
                  </span>
                </div>
                {row.currentWeight != null && (
                  <div className="font-mono text-[11px] tabular-nums text-slate-400 flex-shrink-0">
                    <span className={row.status === 'decreasing' ? 'text-red-300' : 'text-white'}>
                      {Math.round(row.currentWeight)}
                    </span>
                    {row.suggestedWeight != null && row.suggestedWeight !== row.currentWeight && (
                      <>
                        <ArrowRight size={10} className="inline mx-1 text-slate-600 translate-y-[-1px]" />
                        <span className={t.tw}>{Math.round(row.suggestedWeight)}</span>
                      </>
                    )}
                    <span className="text-slate-600 ml-1">lb</span>
                  </div>
                )}
              </div>
              <p className="font-mono text-[10px] text-slate-500 leading-relaxed pl-5">
                {row.reason}
              </p>
            </li>
          );
        })}
      </ul>
      {overflow > 0 && (
        <p className="mt-2 font-mono text-[10px] text-slate-600 text-right">
          +{overflow} more on the Intel sub-tab
        </p>
      )}
    </OpsPanel>
  );
}
